import { Icon } from '@iconify/react';
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import config from '../config';

const SignUpLayer = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState('/assets/images/logo.png');
  const [authBgUrl, setAuthBgUrl] = useState('/assets/images/auth/auth-img.png');

  useEffect(() => {
    fetch(`${config.API_BASE_URL}/api/settings`)
        .then(res => res.json())
        .then(data => {
            if (data.logo_url) setLogoUrl(`${config.API_BASE_URL}${data.logo_url}`);
            if (data.auth_bg_url) setAuthBgUrl(`${config.API_BASE_URL}${data.auth_bg_url}`);
        })
        .catch(err => console.error('Failed to fetch settings:', err));
  }, []);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    agreeTerms: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    if (!formData.full_name) return toast.error('Full Name is required');
    if (!formData.email) return toast.error('Email is required');
    if (!formData.password || formData.password.length < 8) return toast.error('Password must be at least 8 characters');
    if (formData.password !== formData.confirmPassword) return toast.error('Passwords do not match');
    if (!formData.phone) return toast.error('Phone is required');
    if (!formData.agreeTerms) return toast.error('You must agree to the Terms & Conditions');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/auth/register-business`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        // Reset form or navigate to login with a delay
        setTimeout(() => navigate('/sign-in'), 3000);
      } else {
        toast.error(data.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className='auth bg-base d-flex flex-wrap'>
      <div className='auth-left d-lg-block d-none'>
        <div className='d-flex align-items-center flex-column h-100 justify-content-center'>
          <img src={authBgUrl} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      </div>
      <div className='auth-right py-32 px-24 d-flex flex-column justify-content-center'>
        <div className='max-w-464-px mx-auto w-100'>
          <div className='text-center'>
            <Link to='/' className='mb-40 max-w-290-px d-inline-block'>
              <img 
                src={logoUrl} 
                alt='' 
                style={{ maxHeight: '55px', width: 'auto' }} 
                onError={(e) => {
                  e.target.onerror = null; 
                  e.target.src = '/assets/images/logo.png';
                }}
              />
            </Link>
            <h4 className='mb-12'>Become a HubScroll Partner</h4>
            <p className='mb-32 text-secondary-light text-lg'>
              Create your business account to manage your venue.
            </p>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className='icon-field mb-16'>
              <span className='icon top-50 translate-middle-y'>
                <Icon icon='f7:person' />
              </span>
              <input
                type='text'
                name='full_name'
                value={formData.full_name}
                onChange={handleChange}
                className='form-control h-56-px bg-neutral-50 radius-12'
                placeholder='Full Name'
              />
            </div>
            <div className='icon-field mb-16'>
              <span className='icon top-50 translate-middle-y'>
                <Icon icon='mage:email' />
              </span>
              <input
                type='email'
                name='email'
                value={formData.email}
                onChange={handleChange}
                className='form-control h-56-px bg-neutral-50 radius-12'
                placeholder='Email'
              />
            </div>
            <div className='icon-field mb-16'>
              <span className='icon top-50 translate-middle-y'>
                <Icon icon='solar:phone-calling-linear' />
              </span>
              <input
                type='text'
                name='phone'
                value={formData.phone}
                onChange={handleChange}
                className='form-control h-56-px bg-neutral-50 radius-12'
                placeholder='Phone Number'
              />
            </div>
            <div className='icon-field mb-16'>
              <span className='icon top-50 translate-middle-y'>
                <Icon icon='solar:lock-password-broken' />
              </span>
              <input
                type='password'
                name='password'
                value={formData.password}
                onChange={handleChange}
                className='form-control h-56-px bg-neutral-50 radius-12'
                placeholder='Password'
              />
            </div>
            <div className='icon-field mb-16'>
              <span className='icon top-50 translate-middle-y'>
                <Icon icon='solar:lock-password-broken' />
              </span>
              <input
                type='password'
                name='confirmPassword'
                value={formData.confirmPassword}
                onChange={handleChange}
                className='form-control h-56-px bg-neutral-50 radius-12'
                placeholder='Confirm Password'
              />
            </div>
            <div className=''>
              <div className='d-flex justify-content-between gap-2'>
                <div className='form-check style-check d-flex align-items-start'>
                  <input
                    className='form-check-input border border-neutral-300 mt-4'
                    type='checkbox'
                    name='agreeTerms'
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    id='condition'
                  />
                  <label
                    className='form-check-label text-sm'
                    htmlFor='condition'
                  >
                    By creating an account means you agree to the
                    <Link to='#' className='text-primary-600 fw-semibold ms-1'>
                      Terms &amp; Conditions
                    </Link>{" "}
                    and our
                    <Link to='#' className='text-primary-600 fw-semibold ms-1'>
                      Privacy Policy
                    </Link>
                  </label>
                </div>
              </div>
            </div>

            <button
              type='submit'
              disabled={loading}
              className='btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32'
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>

            <div className='mt-32 center-border-horizontal text-center'>
              <span className='bg-base z-1 px-4'>Or sign up with</span>
            </div>
            <div className='mt-32 d-flex align-items-center gap-3'>
              <button
                type='button'
                className='fw-semibold text-primary-light py-16 px-24 w-50 border radius-12 text-md d-flex align-items-center justify-content-center gap-12 line-height-1 bg-hover-primary-50'
              >
                <Icon
                  icon='ic:baseline-facebook'
                  className='text-primary-600 text-xl line-height-1'
                />
                Google
              </button>
              <button
                type='button'
                className='fw-semibold text-primary-light py-16 px-24 w-50 border radius-12 text-md d-flex align-items-center justify-content-center gap-12 line-height-1 bg-hover-primary-50'
              >
                <Icon
                  icon='logos:google-icon'
                  className='text-primary-600 text-xl line-height-1'
                />
                Google
              </button>
            </div>
            <div className='mt-32 text-center text-sm'>
              <p className='mb-0'>
                Already have an account?{" "}
                <Link to='/sign-in' className='text-primary-600 fw-semibold'>
                  Sign In
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default SignUpLayer;
